import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  AdfsAuthentication,
  AuthenticationType,
  GrantType,
  ManagedIdentityAuthentication,
  ManagedIdentityType,
  ModernAuthentication,
  PasswordAuthentication,
  SharePointConnection,
  SharePointConnectionHandlerService
} from "@coso/connection-forms";
import { FormText, ViewMode, FormBuilder, FormGroup } from "@coso/forms";
import { LoadingService } from "@coso/loading";
import { DialogComponent, DialogType, IS_SAAS } from "@coso/utility";
import { forkJoin, of, Subject } from "rxjs";
import { filter, flatMap, map, take, takeUntil } from "rxjs/operators";
import { CustomProblemDetailsTypes, HighAvailabilityModes } from "../../../../models/auto-generated";
import { AUserPermissionsService } from "../../../../services/a-user-permissions.service";
import { ConnectionMapperService } from "../../../../services/connection-mapper.service";
import { EnvironmentService } from "../../../../services/environment.service";
import { ReplicationService } from "../../../../services/replication.service";
import { SettingsService } from "../../../../services/settings.service";
import { SharePointConnectionService } from "../../../../services/sharepoint-connection.service";
import { handleError } from "../../../../utils/handle-error";
import { isRunning } from "../../../../utils/is-running";
import { SettingsValidator } from "../../../../utils/settings-validator";
import { SharePointConnectionForm } from "../models/sharepoint-connection-form";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { IS_DOCKER } from "@coso-replicator/core";
import { resolveSharePointScope } from "../../../../utils/sharepoint-scope";
import { isValidUrl } from "../../../../utils/url-validator";
import { Validators } from "@angular/forms";

@Component({
  standalone:false,
  templateUrl: "./add-edit-sharepoint.component.html",
  styleUrls: ["./add-edit-sharepoint.component.scss"]
})
export class AddEditSharePointComponent implements OnInit, OnDestroy {
  private unsubscribe = new Subject<void>();
  private original: SharePointConnection;
  private originalName: string;

  instanceId: string;
  get title() {
    if (this.isConfigured) {
      if (this.isReadOnly) {
        return "View SharePoint connection";
      } else {
        return "Edit SharePoint connection";
      }
    } else {
      return "Create SharePoint connection";
    }
  }
  formGroup: FormGroup<SharePointConnectionForm>;
  formText: FormText<SharePointConnectionForm>;
  ViewMode = ViewMode;
  isConfigured = false;
  errorMessage: string = undefined;
  structureCreatorError : string = undefined;
  successMessage: string = undefined;
  isReadOnly: boolean;
  isRunning: boolean;
  isFailoverBackupInstance: boolean;
  apiPermission: string = "AllSites.FullControl";

  @ViewChild("validationRef", { static: true }) validationRef: TemplateRef<any>;
  incompatibilities: string[];
  constructor(
    @Inject(IS_DOCKER) public isDocker: boolean,
    @Inject(IS_SAAS)
    public isSaaS: boolean,
    public loadingService: LoadingService,
    public spConnectionHandler: SharePointConnectionHandlerService,
    private fb: FormBuilder,
    private spConnectionService: SharePointConnectionService,
    private route: ActivatedRoute,
    private router: Router,
    private settingsService: SettingsService,
    private userPermissionsService: AUserPermissionsService,
    private connectionMapperService: ConnectionMapperService,
    private replicationService: ReplicationService,
    private modalService: NgbModal,
    private environmentService: EnvironmentService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
    sessionStorage.removeItem("instance-id");
  }

  ngOnInit() {
    this.instanceId = this.route.snapshot.paramMap.get("instance-id");
    sessionStorage.setItem("instance-id", this.instanceId);
    const sharePointId = this.route.snapshot.params["sharepoint-id"];    

    forkJoin([
      this.replicationService.getStatus(this.instanceId),
      this.userPermissionsService.hasLimitedAccess$,
      this.userPermissionsService.isCustomerAgent$,
      this.settingsService.get(this.instanceId)
    ]).subscribe(
      ([status, hasLimitedAccess, isCustomerAgent, settings]) => {
        this.isRunning = isRunning(status);
        this.isReadOnly = hasLimitedAccess || isCustomerAgent || isRunning(status);
        if (this.isSaaS === false) {
          this.isReadOnly = this.isReadOnly || settings.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
          this.isFailoverBackupInstance = settings.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
        }
      },
      (error) => (this.errorMessage = `Something went wrong due to: ${handleError(error)}`)
    );
    this.generateFormGroup(sharePointId);
    if (sharePointId != null) {
      this.isConfigured = true;
      this.settingsService
        .get(this.instanceId)
        .pipe(
          take(1),
          // eslint-disable-next-line eqeqeq
          map((data) => data.sharePoint.sharepointConfig.find((element) => element.key == sharePointId))
        )
        .subscribe((data) => {
          this.originalName = data.name;
          return this.formGroup.controls.name.setValue(data.name);
        });
      this.loadConnection(sharePointId);
    } else {
      this.environmentService.getDefaultClientId().subscribe((defaultClientId) => {
        this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.clientId.setValue(defaultClientId);
      });
    }
  }

  returnToList(message?: string) {
    this.router.navigate(["../../"], {
      relativeTo: this.isConfigured ? this.route.parent : this.route,
      state: { message }
    });
  }

  saveConnection() {
    if (this.isReadOnly) {
      return;
    }
    const connectionFormValue = this.formGroup.getRawValue();
    const internalId = connectionFormValue.sharePoint.internalId;

    this.spConnectionHandler
      .isTestConnectionSuccessfulFor$(internalId)
      .pipe(take(1))
      .subscribe((isSuccessful) => {
        if (isSuccessful) {
          this.save();
          return;
        }

        this.spConnectionHandler.clearStateFor(internalId);
        this.spConnectionHandler
          .testConnectionResultFor$(internalId)
          .pipe(
            filter((r) => r !== undefined),
            take(1)
          )
          .subscribe((result) => {
            if (result.isSuccess) {
              this.save();
            }
          });

        this.spConnectionHandler.testConnection(connectionFormValue.sharePoint);
      });
  }

  private save() {
    this.errorMessage = undefined;
    if (this.isConfigured) {
      const url = this.formGroup.controls.sharePoint.controls.siteCollectionUrl.value;
      if (url !== this.original.siteCollectionUrl) {
        this.loadingService.start("Validating current configuration", "validateOnSiteCollectionUrlChange");
        this.settingsService.get(this.instanceId).subscribe(
          (current) => {
            const key = Number(this.formGroup.getRawValue().sharePoint.id);
            const validationResult = SettingsValidator.validateOnSiteCollectionUrlChange(current, key);
            if (validationResult.valid) {
              this.update();
            } else {
              this.incompatibilities = validationResult.incompatibilities;
              this.errorMessage = undefined;
              const modalRef = this.modalService.open(DialogComponent, {
                keyboard: false,
                ariaLabelledBy: "modalTitle"
              });
              modalRef.componentInstance.options = {
                titleText: "Incompatible Settings",
                type: DialogType.Warning,
                contentTemplateRef: this.validationRef,
                rightButtons: [
                  {
                    label: "No",
                    type: "button",
                    class: "btn btn-dark mr-2",
                    onClick: (modalRef) => {
                      this.errorMessage = `The operation was cancelled.`;
                      modalRef.dismiss();
                    }
                  },
                  {
                    label: "Yes",
                    type: "button",
                    class: "btn btn-primary",
                    onClick: () => {
                      const settings = {
                        ...current,
                        sharePoint: {
                          ...current.sharePoint,
                          sharepointConfig: current.sharePoint.sharepointConfig.map((config) => {
                            if (config.key === key) {
                              return {
                                ...config,
                                userMapping: { ...config.userMapping, userPairs: [] },
                                permissionMapping: { ...config.permissionMapping, permissionPairs: [] },
                                specialPermissions: []
                              };
                            } else {
                              return config;
                            }
                          })
                        }
                      };
                      this.settingsService.save(this.instanceId, settings).subscribe(
                        () => {
                          this.update();
                          modalRef.dismiss();
                        },
                        (error) => {
                          this.errorMessage = `Failed to save settings due to: ${handleError(error)}`;
                          modalRef.dismiss();
                        }
                      );
                    }
                  }
                ],
                onClose: () => modalRef.dismiss()
              };
            }
            this.loadingService.stop("validateOnSiteCollectionUrlChange");
          },
          (error) => {
            this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
            this.loadingService.stop("validateOnSiteCollectionUrlChange");
          }
        );
      } else {
        this.update();
      }
    } else {
      this.create();
    }
  }

  private create() {
    this.loadingService.start("Creating SharePoint site collection", "createSpConnection");
    this.spConnectionService
      .createConnection(this.instanceId, this.formGroup.controls.name.value)
      .pipe(
        flatMap((item) => {
          const connection = this.connectionMapperService.mapFromSharePointConnection(this.formGroup.getRawValue().sharePoint);
          return this.spConnectionService.updateConnection(this.instanceId, item.key, connection);
        })
      )
      .subscribe(
        () => {
          this.loadingService.stop("createSpConnection");
          this.returnToList(`SharePoint site collection '${this.formGroup.controls.name.value}' was successfully created.`);
        },
        (error) => {
          this.errorMessage = `Failed to create SharePoint connection '${this.formGroup.controls.name.value}' due to: ${handleError(error)}`;
          this.loadingService.stop("createSpConnection");
        }
      );
  }

  private update() {
    this.loadingService.start("Updating SharePoint site collection", "updateSpConnection");
    const connection = this.connectionMapperService.mapFromSharePointConnection(this.formGroup.getRawValue().sharePoint);
    this.spConnectionService
      .updateConnection(this.instanceId, Number(this.formGroup.value.sharePoint.id), connection)
      .pipe(
        flatMap(() => {
          if (this.formGroup.value.name !== this.originalName) {
            return this.settingsService.get(this.instanceId).pipe(
              flatMap((current) => {
                const settings = {
                  ...current,
                  sharePoint: {
                    ...current.sharePoint,
                    sharepointConfig: current.sharePoint.sharepointConfig.map((element) => {
                      if (element.key === Number(this.formGroup.value.sharePoint.id)) {
                        return { ...element, name: this.formGroup.value.name };
                      } else {
                        return element;
                      }
                    })
                  }
                };
                return this.settingsService.save(this.instanceId, settings);
              })
            );
          }
          return of(undefined);
        })
      )
      .subscribe(
        () => {
          this.loadingService.stop("updateSpConnection");
          this.returnToList(`SharePoint site collection '${this.formGroup.controls.name.value}' was successfully updated.`);
        },
        (error) => {
          if(error?.status == 400 && error?.error?.type == CustomProblemDetailsTypes.StructureCreatorUpdateFailure){
            this.structureCreatorError = `${handleError(error)}.`;
            this.successMessage = `SharePoint site collection '${this.formGroup.controls.name.value}' was successfully updated.`;
          }
          else{
            this.errorMessage = `Failed to update SharePoint connection '${this.formGroup.controls.name.value}' due to: ${handleError(error)}`;
          }
          this.loadingService.stop("updateSpConnection");
        }
      );
  }

  private loadConnection(connectionId: number) {
    this.loadingService.start("Loading SharePoint connection", "loadSpConnection");
    forkJoin([this.environmentService.getDefaultClientId(), this.spConnectionService.getConnection(this.instanceId, connectionId)]).subscribe(
      ([defaultClientId, data]) => {
        const connection = this.connectionMapperService.mapToSharePointConnection(data, connectionId);
        this.original = connection;
        this.formGroup.controls.sharePoint.patchValue(connection);
        if (defaultClientId && !connection.modernAuthentication.clientId) {
          this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.clientId.setValue(defaultClientId);
        }
        if (connection.managedIdentityAuthentication && connection.managedIdentityAuthentication.clientId) {
          this.formGroup.controls.sharePoint.controls.managedIdentityAuthentication.controls.type.setValue(ManagedIdentityType.UserAssigned);
          this.formGroup.controls.sharePoint.controls.managedIdentityAuthentication.controls.clientId.setValue(connection.managedIdentityAuthentication.clientId);
        }
        if (connection.authenticationType === AuthenticationType.ManagedIdentity && connection.managedIdentityAuthentication.clientId) {
          this.formGroup.controls.sharePoint.controls.managedIdentityAuthentication.controls.type.setValue(ManagedIdentityType.UserAssigned);
          this.formGroup.controls.sharePoint.controls.managedIdentityAuthentication.controls.clientId.setValue(connection.managedIdentityAuthentication.clientId);
        }
        this.loadingService.stop("loadSpConnection");
      },
      (error) => {
        this.errorMessage = `Failed to get SharePoint connection due to: ${handleError(error)}`;
        this.loadingService.stop("loadSpConnection");
      }
    );
  }

  private generateFormGroup(sharePointId: any) {
    this.formGroup = this.fb.group<SharePointConnectionForm>({
      name: this.fb.control<string>(null, [Validators.required, Validators.maxLength(256)]),
      sharePoint: this.fb.group<SharePointConnection>({
        id: this.fb.control<string>(sharePointId),
        siteCollectionUrl: this.fb.control<string>(null),
        isOnline: this.fb.control<boolean>(null),
        authenticationType: this.fb.control<AuthenticationType>(AuthenticationType.Modern),
        modernAuthentication: this.fb.group<ModernAuthentication>({
          tenantId: this.fb.control<string>(null),
          azureEndpoint: this.fb.control<string>("https://login.microsoftonline.com/"),
          clientId: this.fb.control<string>(null),
          certificateThumbprint: this.fb.control<string>(null),
          useDefaultCertificate: this.fb.control<boolean>(true, [Validators.required]),
          grantType: this.fb.control(GrantType.ClientCredentials),
          clientSecret: this.fb.control(null),
          encryptedToken: this.fb.control(null),
          scope: this.fb.control("offline_access"),
          changeAzureAdApp: this.fb.control<boolean>(false),
          useDefaultAzureAdApp: this.fb.control(true),
          saasConsentCallbackGrantScope: this.fb.control(this.instanceId),
          apiPermission: this.fb.control<string>("AllSites.FullControl", [Validators.required]),
        }),
        passwordAuthentication: this.fb.group<PasswordAuthentication>({
          username: this.fb.control<string>(null),
          password: this.fb.control<string>(null),
          changePassword: this.fb.control<boolean>(false)
        }),
        adfsAuthentication: this.fb.group<AdfsAuthentication>({
          adfsUrl: this.fb.control<string>(null),
          realmUrn: this.fb.control<string>(null),
          passwordAuthentication: this.fb.group<PasswordAuthentication>({
            username: this.fb.control<string>(null),
            password: this.fb.control<string>(null),
            changePassword: this.fb.control<boolean>(false)
          })
        }),
        managedIdentityAuthentication: this.fb.group<ManagedIdentityAuthentication>({
          type: this.fb.control(ManagedIdentityType.SystemAssigned),
          clientId: this.fb.control<string>(null)
        })
      })
    });
    this.formGroup.controls.sharePoint.controls.siteCollectionUrl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter((u) => !!u)
      )
      .subscribe((value) => {
        if (this.formGroup.controls.sharePoint.controls.siteCollectionUrl.valid) {
          this.setScope(value)
        }
      });
    this.formGroup.controls.sharePoint.controls.authenticationType.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter((u) => u !== AuthenticationType.Adfs)
      )
      .subscribe(() => this.formGroup.controls.sharePoint.controls.adfsAuthentication.reset());
    
    this.formGroup.controls.sharePoint.controls.modernAuthentication?.controls.apiPermission?.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter((u) => !!u)
      )
      .subscribe((value) => {
        this.apiPermission = value;
        this.setScope(this.formGroup.controls.sharePoint.controls.siteCollectionUrl.value);
      });

    this.formText = {
      name: {
        label: "Name",
        placeholder: "Name",
        popover: "The name is just a field which appears later in the list of connections. By giving the connection a meaningful name, it is easier to find it later in the list.",
        errors: {
          required: "The Name field is required",
          maxlength: "The Name field must be at max 256 characters long."
        }
      }
    };
  }

  private setScope(url: string) {    
    if (isValidUrl(url)){
      this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.scope.setValue(resolveSharePointScope(url, this.apiPermission));
    }    
  }
}
